// ── FARM PAGE DATA — the pure shaping behind one customer's Farm page ─────────
//
// One Farm page per customer, the same page for the agent and for the farmer.
// Every function here takes rows the page already read (under the viewer's own
// session, farmer-scoped by RLS) and hands back what the page draws. No fetch,
// no React, no dates of its own: the crop year is always passed in.
//
// The owner's rules, each enforced here rather than at the draw:
//   - silage and grain are never merged: a crop line is crop + (silage or not),
//     so corn grain and corn silage are always two lines, silage in wet ton/ac;
//   - dry beans read lb/ac; any other crop shows no guessed unit;
//   - claims in progress are status facts (filed, adjusting, confirmed), never an
//     amount; a claim's dollars show only once the carrier has PAID them (actual
//     paid history, the owner's call 2026-11-02), never a pending, escrowed or
//     estimated figure; ARC/PLC is elections and FSA base acres, never dollars;
//   - coverage dollars (liability) are an insurance fact and may be shown.
import { unitLabel, isSilageCrop } from "../ui/units";
import { cropWords } from "../ui/plainWords";

// Production record types that are a grower's ACTUAL yield (the same set the APH
// history colours as actual). T-yields and assigned years are not production.
export const ACTUAL_RECORD_TYPES = new Set(["A", "PA", "PW", "WY", "AY", "BF", "DA"]);

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const up = (s) => String(s == null ? "" : s).trim().toUpperCase();
const titleCase = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// Production files name corn silage by its abbreviation ("GSS"), coverage rows by
// RMA's type code ("026"). Both mean the same line.
const SILAGE_TYPE_WORDS = new Set(["GSS", "SILAGE", "SIL", "026"]);
export function silageFlag(crop, typeCode, typeWord) {
  if (isSilageCrop(crop, typeCode)) return true;
  return up(crop) === "CORN" && SILAGE_TYPE_WORDS.has(up(typeWord));
}
// The type code the unit rules read: silage carries 026 so unitLabel says wet ton/ac.
const unitTypeCode = (silage, typeCode) => (silage ? "026" : typeCode);

// "Corn grain", "Corn silage", "Soybeans", "Dry beans" — the line's name, in
// sentence case (plainWords' cropWords), never "Dry Beans".
export function cropLineLabel(crop, silage) {
  const c = up(crop);
  if (c === "CORN") return silage ? "Corn silage" : "Corn grain";
  if (silage && !/SILAGE/.test(c)) return cropWords(`${crop} silage`);
  return cropWords(crop) || "Crop";
}
export const cropLineKey = (crop, silage) => `${up(crop)}|${silage ? "SILAGE" : "GRAIN"}`;
export const yieldUnit = (crop, silage) => unitLabel(crop, "yield", { typeCode: unitTypeCode(silage, null) });

export const fmtAcres = (v) => (num(v) == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
export const fmtDollars = (v) => (num(v) == null ? "—" : (Number(v) < 0 ? "-$" : "$") + Math.abs(Math.round(Number(v))).toLocaleString("en-US"));
export const fmtYield = (v) => (num(v) == null ? "—" : Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 }));
export const pct = (v) => (num(v) == null ? "" : `${Math.round(Number(v) * 100)}%`);

// ── 1. COVERAGE, THE CARD'S OWN NUMBERS ─────────────────────────────────────
// fig: what App.jsx's farmCoverageFigures() returns: { lines, springYear, fallYear,
// fallPending }. Every number on a line is the farmer card's (coverageRowFigures:
// planted revenue guarantee, guarantee per acre, bushel guarantee, gross and farmer
// premium), so the Farm page and Policy detail cannot disagree. This only groups:
// season, then crop line (grain and silage never merged), then the card's rows.
// A total is the sum of the rows that HAVE the figure; a line where none do reads
// null (Pending), never 0. Bushels sum only inside one crop line, so tons, lbs and
// bushels never add.
// lobs: the viewer's granted lines of business (portal), or null for an agent.
export const FALL_CROP_SET = new Set(["WHEAT", "WINTER WHEAT", "BARLEY", "TRITICALE", "RYE", "SPELT"]);
// PRF's grid rows carry a pasture crop name; they belong to PRF, not to spring crops.
export function lobOfCrop(crop) {
  const c = up(crop);
  if (/PASTURE|RANGELAND|\bPRF\b/.test(c)) return "prf";
  return FALL_CROP_SET.has(c) ? "fall" : "spring";
}
// May this viewer see a row of this line of business? An agent (lobs null) sees all.
export function lobAllows(lobs, { crop = null, policyType = null } = {}) {
  if (!lobs) return true;
  const pt = String(policyType || "crop").toLowerCase();
  if (pt === "drp" || pt === "lrp" || pt === "prf") return lobs.has(pt);
  return lobs.has(lobOfCrop(crop));
}
const addOrNull = (a, b) => (b == null ? a : (a == null ? 0 : a) + Number(b));
export function coverageFromFigures(fig, { agentView = false, lobs = null } = {}) {
  if (!fig) return null;
  const seasons = [];
  for (const season of ["spring", "fall"]) {
    if (lobs && !lobs.has(season)) continue;
    const ls = (fig.lines || []).filter((l) => l && l.season === season);
    const year = season === "spring" ? fig.springYear : fig.fallYear;
    if (!ls.length) continue;
    const groups = new Map();
    for (const l of ls) {
      const silage = !!l.silage || silageFlag(l.crop, null, l.typeRaw);
      const k = cropLineKey(l.crop, silage);
      let g = groups.get(k);
      if (!g) {
        g = { key: `${season}|${k}`, crop: up(l.crop), silage, label: cropLineLabel(l.crop, silage), unit: l.unit || "bu", yieldUnit: yieldUnit(l.crop, silage),
          totalUnit: unitLabel(l.crop, "total", { typeCode: unitTypeCode(silage, null) }) || l.unit || null,
          rows: [], revenueGuarantee: null, buGuarantee: null, plantedAcres: null, ppAcres: 0, uiAcres: 0, grossPremium: null, farmerPremium: null, pending: false };
        groups.set(k, g);
      }
      const type = agentView ? l.typeRaw : l.typeFarmer;
      g.rows.push({
        key: l.key, county: titleCase(l.county), type: type || null, organic: !!l.organic, plan: l.plan || null,
        coverageLevel: num(l.coverageLevel), coverageText: l.coverageText || (num(l.coverageLevel) != null ? pct(l.coverageLevel) : null),
        unitStructure: l.unitStructure || null, price: num(l.price), aph: num(l.aph), aphWeighted: num(l.aphWeighted),
        plantedAcres: num(l.plantedAcres), ppAcres: num(l.ppAcres) || 0, uiAcres: num(l.uiAcres) || 0,
        revenueGuarantee: num(l.revenueGuarantee), guarPerAcre: num(l.guarPerAcre), buGuarantee: num(l.buGuarantee), buStated: !!l.buStated,
        grossPremium: num(l.grossPremium), farmerPremium: num(l.farmerPremium), pending: !!l.pending, pendingReason: l.pendingReason || null,
        units: (l.units || []).map((u) => ({ ...u, county: titleCase(u.county) })),
        endorsements: (l.endorsements || []).map((e) => ({ ...e })),
      });
      g.revenueGuarantee = addOrNull(g.revenueGuarantee, num(l.revenueGuarantee));
      g.buGuarantee = addOrNull(g.buGuarantee, num(l.buGuarantee));
      g.plantedAcres = addOrNull(g.plantedAcres, num(l.plantedAcres));
      g.ppAcres += num(l.ppAcres) || 0; g.uiAcres += num(l.uiAcres) || 0;
      g.grossPremium = addOrNull(g.grossPremium, num(l.grossPremium));
      g.farmerPremium = addOrNull(g.farmerPremium, num(l.farmerPremium));
      for (const e of l.endorsements || []) {
        g.grossPremium = addOrNull(g.grossPremium, num(e.grossPremium));
        g.farmerPremium = addOrNull(g.farmerPremium, num(e.farmerPremium));
      }
      if (l.pending) g.pending = true;
    }
    const lines = [...groups.values()].sort((a, b) => (b.revenueGuarantee || 0) - (a.revenueGuarantee || 0) || a.label.localeCompare(b.label));
    const sum = (f) => lines.reduce((s, l) => addOrNull(s, l[f]), null);
    seasons.push({ key: season, label: season === "spring" ? "Spring crops" : "Fall crops", year: year ?? null,
      pending: season === "fall" && !!fig.fallPending, lines,
      revenueGuarantee: sum("revenueGuarantee"), plantedAcres: sum("plantedAcres"), farmerPremium: sum("farmerPremium"),
      ppAcres: lines.reduce((s, l) => s + l.ppAcres, 0), uiAcres: lines.reduce((s, l) => s + l.uiAcres, 0) });
  }
  return { seasons };
}

// ── 2. FIELDS ───────────────────────────────────────────────────────────────
// fields: policy_fields rows. Acres by crop line for the crop year, and the FSA
// farms they sit on. Grain and silage fields are separate lines.
export function fieldsByCrop(fields = [], cropYear) {
  const rows = (fields || []).filter((f) => f && (cropYear == null || Number(f.crop_year) === Number(cropYear)));
  const byLine = new Map(), byFarm = new Map();
  for (const f of rows) {
    const silage = silageFlag(f.crop, f.type_code);
    const k = cropLineKey(f.crop || "UNKNOWN", silage);
    // A row with no acres recorded (a PRF grid row) adds a field, never 0 acres: a line
    // or farm with no recorded acres reads "acres not recorded", not "0.0 ac".
    const a = num(f.acres);
    const l = byLine.get(k) || { key: k, label: f.crop ? cropLineLabel(f.crop, silage) : "Crop not recorded", acres: null, fields: 0 };
    if (a != null) l.acres = (l.acres || 0) + a; l.fields += 1; byLine.set(k, l);
    const fk = f.fsn ? String(f.fsn) : "—";
    const fm = byFarm.get(fk) || { fsn: fk, county: titleCase(f.county || f.insured_county), acres: null, fields: 0, tracts: new Set() };
    if (a != null) fm.acres = (fm.acres || 0) + a; fm.fields += 1; if (f.tract) fm.tracts.add(String(f.tract)); byFarm.set(fk, fm);
  }
  const r2 = (v) => (v == null ? null : Math.round(v * 100) / 100);
  return {
    crops: [...byLine.values()].map((l) => ({ ...l, acres: r2(l.acres) })).sort((a, b) => (b.acres || 0) - (a.acres || 0)),
    farms: [...byFarm.values()].map((f) => ({ fsn: f.fsn, county: f.county, acres: r2(f.acres), fields: f.fields, tracts: f.tracts.size }))
      .sort((a, b) => (b.acres || 0) - (a.acres || 0)),
    acres: r2(rows.reduce((s, f) => s + (num(f.acres) || 0), 0)),
    fields: rows.length,
  };
}

// ── 3a. PRODUCTION ──────────────────────────────────────────────────────────
// production_history rows -> one line per crop line, newest actual years first.
// Yield = production / acres where both are recorded, else the acre-weighted
// stated yield. The stored unit_of_measure is NOT used: the carrier file writes
// "BU" on grapes, potatoes and dry beans alike, so the unit comes from the crop.
export function productionByCrop(rows = [], { years = 4 } = {}) {
  const lines = new Map();
  for (const r of rows || []) {
    // Silage rows carry excluded_from_avg with reason SILAGE so a grain average never
    // takes them in. Here silage is its own line, so those rows are kept; every other
    // exclusion (Z, T, PP, I ...) still stays off.
    if (!r) continue;
    const silage = silageFlag(r.crop, null, r.type);
    // ...and only when the row itself reads as silage, so it can never land on a grain line.
    if (r.excluded_from_avg && !(silage && up(r.exclusion_reason) === "SILAGE")) continue;
    if (!ACTUAL_RECORD_TYPES.has(up(r.record_type))) continue;
    const yr = num(r.crop_year); if (yr == null) continue;
    const k = cropLineKey(r.crop, silage);
    const l = lines.get(k) || { key: k, crop: up(r.crop), silage, label: cropLineLabel(r.crop, silage), unit: yieldUnit(r.crop, silage), byYear: new Map() };
    const y = l.byYear.get(yr) || { year: yr, acres: 0, prod: 0, prodAcres: 0, wSum: 0, wAcres: 0 };
    const ac = num(r.acres) || 0, pr = num(r.production), ypa = num(r.yield_per_acre);
    y.acres += ac;
    if (pr != null && ac > 0) { y.prod += pr; y.prodAcres += ac; }
    else if (ypa != null && ac > 0) { y.wSum += ypa * ac; y.wAcres += ac; }
    l.byYear.set(yr, y); lines.set(k, l);
  }
  return [...lines.values()].map((l) => ({
    key: l.key, crop: l.crop, silage: l.silage, label: l.label, unit: l.unit,
    years: [...l.byYear.values()].sort((a, b) => b.year - a.year).slice(0, years).map((y) => {
      const acres = y.prodAcres + y.wAcres;
      const yld = acres > 0 ? (y.prod + y.wSum) / acres : null;
      return { year: y.year, acres: Math.round(y.acres * 100) / 100, yield: yld == null ? null : Math.round(yld * 10) / 10 };
    }),
  })).filter((l) => l.years.length).sort((a, b) => a.label.localeCompare(b.label));
}

// ── 3b. CLAIMS, AS FACTS ────────────────────────────────────────────────────
// farmer.claims rows (one per disbursement) folded to one row per claim. Only
// status, crop, county, year and dates, never an amount: paid dollars live in
// paidClaims below, which counts nothing until the carrier has paid it.
// Livestock (LRP) claims stay agent-only, the same rule the Claims page keeps.
export const CLAIM_STEPS = ["Notice filed", "Adjusting", "Confirmed", "Paid"];
const STEP_OF = { "Notice Filed": 0, Filed: 0, Open: 1, Adjustment: 1, "In Adjustment": 1, Pending: 1, Verified: 2, Paid: 3, Closed: 3, Released: 3 };
export function claimStep(status) {
  const at = STEP_OF[status];
  if (at == null) return { step: null, label: status || "Status not recorded" };
  if (status === "Closed" || status === "Released") return { step: 3, label: "Closed, no payment" };
  return { step: at, label: CLAIM_STEPS[at] };
}
export function claimFacts(claims = [], { agentView = false, cropYear = null } = {}) {
  const m = new Map();
  for (const c of claims || []) {
    if (!c) continue;
    if (c.policyType === "lrp" && !agentView) continue;
    if (cropYear != null && c.cropYear != null && Number(c.cropYear) < Number(cropYear) - 1) continue;
    const k = `${c.policyId || ""}|${c.cropYear || ""}|${c.claimNumber || c.lossKey || ""}`;
    const g = m.get(k) || { key: k, claimNumber: c.claimNumber || null, cropYear: c.cropYear || null, crops: new Set(), counties: new Set(),
      statuses: new Set(), noticeDate: null, lastDate: null, policyType: c.policyType || "crop" };
    if (c.crop) g.crops.add(cropWords(c.crop));
    if (c.county) g.counties.add(titleCase(c.county));
    if (c.status) g.statuses.add(c.status);
    const nd = c.noticeDate || c.claimDate || null;
    if (nd && (!g.noticeDate || nd < g.noticeDate)) g.noticeDate = String(nd).slice(0, 10);
    const ld = c.checkDate || c.claimDate || null;
    if (ld && (!g.lastDate || ld > g.lastDate)) g.lastDate = String(ld).slice(0, 10);
    m.set(k, g);
  }
  return [...m.values()].map((g) => {
    // The claim is as far along as its furthest disbursement.
    const best = [...g.statuses].map((s) => ({ s, ...claimStep(s) })).sort((a, b) => (b.step ?? -1) - (a.step ?? -1))[0] || claimStep(null);
    return { key: g.key, claimNumber: g.claimNumber, cropYear: g.cropYear, crop: [...g.crops].join(", ") || (g.policyType === "crop" ? "Crop" : up(g.policyType)),
      county: [...g.counties].join(", "), step: best.step, status: best.label, noticeDate: g.noticeDate, lastDate: g.lastDate };
  }).sort((a, b) => (Number(b.cropYear) || 0) - (Number(a.cropYear) || 0) || String(b.lastDate || "").localeCompare(String(a.lastDate || "")));
}

// ── 3c. PAID CLAIMS HISTORY ─────────────────────────────────────────────────
// What the carrier actually paid, every year on file. One row per claim, keyed on
// the AIP's loss key (one claim number can carry two claims) and else the claim
// number. Only disbursements with status Paid count: an unfunded escrow (code D,
// Pending), an open or verified-but-unpaid line, and any estimate never do. The
// sum of a claim's Paid lines matches the carrier's own stated claim total on
// every claim that carries one. Money credited to premium or taken as a fee is
// still money paid on the claim, so it counts, and is named apart from the check.
// LRP paid claims show to the farmer too, like DRP: "I do want farmers to be able
// to see their actual paid claims history" (the owner, 2026-11-02). Claims still in
// progress for LRP stay agent-only, as above.
// P (and U, its livestock form) is loss applied to premium on ANOTHER policy of the
// grower's (RMA's wording): named apart from M, credit on this policy's premium.
const PAY_KIND = { E: "check", L: "check", M: "credit", V: "credit", P: "otherPolicy", U: "otherPolicy" };
export const PAY_KIND_WORDS = { check: "Check", credit: "Credited to premium", otherPolicy: "Credited to another policy", other: "Fees and other" };
export function paidClaims(claims = [], { agentView = false } = {}) {
  const m = new Map();
  for (const c of claims || []) {
    if (!c || c.status !== "Paid") continue;
    const amt = Number(c.indemnityAmount != null ? c.indemnityAmount : c.amount);
    if (!isFinite(amt) || amt === 0) continue;
    const k = `${c.policyId || ""}|${c.cropYear || ""}|${c.lossKey || c.claimNumber || ""}`;
    const g = m.get(k) || { key: k, cropYear: c.cropYear || null, crops: new Set(), counties: new Set(), paidDate: null,
      amount: 0, parts: { check: 0, credit: 0, otherPolicy: 0, other: 0 }, policyType: c.policyType || "crop", quarters: new Set() };
    if (c.quarter && /^Q[1-4]\s+\d{4}$/.test(String(c.quarter).trim())) g.quarters.add(String(c.quarter).trim());
    if (c.crop) g.crops.add(cropWords(c.crop));
    if (c.county) g.counties.add(titleCase(c.county));
    const d = c.checkDate ? String(c.checkDate).slice(0, 10) : null;
    if (d && (!g.paidDate || d > g.paidDate)) g.paidDate = d;
    g.amount += amt;
    g.parts[PAY_KIND[up(c.lossCode)] || "other"] += amt;
    m.set(k, g);
  }
  const rows = [...m.values()].filter((g) => Math.round(g.amount) !== 0).map((g) => ({
    key: g.key, cropYear: g.cropYear, crop: [...g.crops].join(", ") || (g.policyType === "crop" ? "Crop" : up(g.policyType)),
    county: [...g.counties].join(", "), paidDate: g.paidDate, amount: Math.round(g.amount), quarter: [...g.quarters].join(", ") || null,
    // The split prints only when the claim was paid more than one way.
    parts: Object.entries(g.parts).filter(([, v]) => Math.round(v) !== 0).map(([kind, v]) => ({ kind, label: PAY_KIND_WORDS[kind], amount: Math.round(v) })),
  })).sort((a, b) => (Number(b.cropYear) || 0) - (Number(a.cropYear) || 0) || String(b.paidDate || "").localeCompare(String(a.paidDate || "")));
  // Kevin, 2026-11-03: "Paid claims needs to be split out by year." One group per crop
  // year (the reinsurance year for DRP and LRP), newest first, each with its own total.
  const years = new Map();
  for (const r of rows) { const y = Number(r.cropYear) > 0 ? Number(r.cropYear) : null; const k = y == null ? "none" : String(y);
    if (!years.has(k)) years.set(k, { year: y, total: 0, rows: [] }); const g = years.get(k); g.total += r.amount; g.rows.push(r); }
  const byYear = [...years.values()].sort((a, b) => (b.year ?? -1) - (a.year ?? -1));
  return { rows, byYear, total: rows.reduce((s, r) => s + r.amount, 0), firstYear: (() => { const ys = rows.map((r) => Number(r.cropYear)).filter((y) => isFinite(y) && y > 0); return ys.length ? Math.min(...ys) : null; })() };
}

// ── 4. FARM PROGRAMS ────────────────────────────────────────────────────────
// The FSA-156EZ model (src/fsa156ez.js baseAcreModel) -> one row per FSA farm and
// crop: the program FSA's record shows and the base acres. Acres only. Never a
// payment, never an estimate: a crop with no base acres on file is absent.
const SOURCE_WORD = { elected: "Elected", default: "FSA default", ccc866: "Signed CCC-866" };
export function programRows(model) {
  if (!model || !Array.isArray(model.farms)) return [];
  const rows = [];
  for (const f of model.farms) {
    for (const [crop, c] of Object.entries(f.byCrop || {})) {
      if (num(c.base_acres) == null) continue;
      rows.push({ key: `${f.farm_number}|${crop}`, farm: String(f.farm_number), county: titleCase(f.admin_county), crop: cropWords(crop),
        program: c.program || "Not on record", source: SOURCE_WORD[c.source] || "", baseAcres: num(c.base_acres) });
    }
  }
  return rows.sort((a, b) => Number(a.farm) - Number(b.farm) || a.crop.localeCompare(b.crop));
}

// ── 6. KEY DATES ────────────────────────────────────────────────────────────
// upcomingInsuranceDates() output (src/home/insuranceDates.js, the feed Home uses)
// already scoped to this farm's own fields. Kept to the next `limit`.
export function keyDates(events = [], limit = 8) {
  return (events || []).slice(0, limit).map((e) => ({ key: e.id, date: e.date, daysAway: e.daysAway, title: e.title, sub: e.sub, varies: !!e.varies }));
}

// The ids every read on the page is scoped by. Taken from the farmer object the
// caller already holds (RLS-scoped in the portal). Never widened, never blank-filled.
export function farmIds(farmer) {
  if (!farmer) return { farmerIds: [], policyIds: [] };
  const farmerIds = [...new Set([farmer.id, ...(farmer.allFarmerDbIds || []), ...(farmer._allFarmerIds || [])].filter(Boolean).map(String))];
  const policyIds = [...new Set([...(farmer.allPolicyIds || []), ...(farmer._allPolicyIds || [])].filter(Boolean).map(String))];
  return { farmerIds, policyIds };
}
